import i18n from "i18next";
import { useEffect } from "react";
import { useEvent } from "./useEvent";
import { useLocalePreference } from "./useLocalePreference";

/**
 * Reconciles the active UI language with the event's enabled languages.
 *
 * The initial locale (src/config/i18n.ts) is resolved synchronously at startup, before any
 * event is loaded, and falls back to a brand default ("en"/"lt"). Guests never hit this because
 * the landing page forces them to pick an enabled language. Admins and oracles skip that gate, so
 * an event with the brand-default language disabled (e.g. Spanish-only) would leave them on
 * English — silently editing the wrong copy fields.
 *
 * This only switches when the current language is NOT one of the event's enabled languages, so a
 * valid existing selection (stored or explicitly chosen) is always preserved.
 */
export const useEnsureEventLocale = () => {
  const [event] = useEvent();
  const { setLocale } = useLocalePreference();
  const locales = event?.locales;

  useEffect(() => {
    if (!locales) return;
    const enabled = (Object.keys(locales) as AppLocale[]).filter((locale) => locales[locale] === true);
    if (enabled.length === 0) return; // misconfigured event — leave the UI as-is
    const current = i18n.language as AppLocale;
    if (enabled.includes(current)) return; // current selection is valid — no change
    setLocale(enabled[0]);
  }, [locales]);
};
